import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { DetailsContent } from "components/app_layout/DetailsLayout";
import { LoadingState } from "components/app_layout/LoadingState";
import { WinThemeList } from "components/bid_module/win_themes/WinThemeList";
import { toast } from "components/ui/toast/Toast";
import { copyToClipboard } from "lib/clipboard";
import { BidReport, useBidReport } from "lib/hooks/api/bid/bid_reports/useBidReport";
import { BidDetails } from "lib/hooks/api/bid/bids/useBid";
import { EventNames, useTracking } from "lib/tracking";
import { BidReportType } from "../../../lib/types/graphQLEnums";
import { CollapseGroup } from "../common_report_components/ReportCollapse";
import { ReportNoticeDetails } from "../common_report_components/ReportNoticeDetails";
import QualificationQuestions from "./QualificationQuestions";
import { ShareQualificationReportDropdown } from "./ShareQualificationReportDropdown";

type Props = {
  bidReportId: string;
};

export function PublicQualificationReport({ bidReportId }: Props) {
  const { data: bidInfo, isLoading: isLoadingBidInfo } = useBidReport(bidReportId);

  if (isLoadingBidInfo) {
    return (
      <LoadingContainer>
        <LoadingState
          title="Loading qualification report"
          description="Please wait while we load the report."
        />
      </LoadingContainer>
    );
  }

  if (!bidInfo) {
    return (
      <LoadingContainer>
        <Text h2>Report not found</Text>
        <Text color="sysTextSecondary">
          This report may have been deleted or the link may be invalid.
        </Text>
      </LoadingContainer>
    );
  }

  const bidId = bidInfo.bid?.id ?? "";
  const bidName = bidInfo.bid?.name ?? "";
  const noticeId = bidInfo.notice?.guid;
  const noticeTitle = bidInfo.notice?.name ?? undefined;
  const buyerName = bidInfo.notice?.buyers?.[0]?.name;
  const buyerId = bidInfo.notice?.buyers?.[0]?.guid;
  const bid = bidInfo.bid;
  const winThemes = bid && "winThemes" in bid ? bid.winThemes : [];

  return (
    <PublicQualificationReportPage>
      <PublicQualificationReportHeader
        bidId={bidId}
        bidName={bidName}
        bidReportId={bidReportId}
        noticeId={noticeId}
        notice={bidInfo.notice}
        winThemes={winThemes}
      />
      <PageContent>
        <Sections>
          <ReportNoticeDetails
            title="Qualification report"
            noticeId={noticeId}
            noticeTitle={noticeTitle}
            buyerName={buyerName}
            buyerId={buyerId}
            reportId={bidReportId}
            bidId={bidId}
            bidName={bidName}
            showAskAI={false}
            hasEditAccess={false}
            reportType={BidReportType.Qualification}
          />
          <CollapseGroup>
            <QualificationQuestions
              bidId={bidId}
              bidReportId={bidReportId}
              noticeId={noticeId}
              noticeTitle={noticeTitle}
              hasEditAccess={false}
              resourceName={bidName}
              disableLinks
            />
            <Flex column gap={24}>
              <Text h2>Win themes</Text>
              <WinThemeList bidId={bidId} bidName={bidName} winThemes={winThemes} disableLinks />
            </Flex>
          </CollapseGroup>
        </Sections>
      </PageContent>
    </PublicQualificationReportPage>
  );
}

function PublicQualificationReportHeader({
  bidId,
  bidName,
  bidReportId,
  noticeId,
  notice,
  winThemes,
}: {
  bidId: string;
  bidName?: string;
  bidReportId: string;
  noticeId?: string;
  notice: BidReport["notice"];
  winThemes: BidDetails["winThemes"];
}) {
  const { logEvent } = useTracking();

  const copyShareableUrl = async (): Promise<boolean> => {
    const copied = await copyToClipboard({ plain: window.location.href });

    if (!copied) {
      toast.error("Failed to copy link to clipboard");
      return false;
    }

    logEvent(EventNames.qualReportLinkCopied, { "Action type": "Link clicked" });
    return true;
  };

  return (
    <ActionBar>
      <ViewOnlyText>View-only</ViewOnlyText>
      <ShareQualificationReportDropdown
        bidId={bidId}
        bidName={bidName}
        bidReportId={bidReportId}
        noticeGuid={noticeId}
        notice={notice}
        winThemes={winThemes}
        onCopyLink={copyShareableUrl}
      />
    </ActionBar>
  );
}

const PublicQualificationReportPage = styled(Flex)({
  flexDirection: "column",
  flex: 1,
});

const PageContent = styled(DetailsContent)({
  display: "flex",
  paddingTop: 0,
});

const Sections = styled.div({
  display: "flex",
  flexDirection: "column",
  padding: "48px 32px",
  gap: 64,
  width: "100%",
  maxWidth: 1200,
  marginLeft: "auto",
  marginRight: "auto",
});

const LoadingContainer = styled(Flex)({
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
  gap: 16,
});

const ActionBar = styled(Flex)(({ theme }) => ({
  alignItems: "center",
  justifyContent: "flex-end",
  gap: 8,
  padding: "8px 24px",
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
}));

const ViewOnlyText = styled(Text)(({ theme }) => ({
  color: theme.colors.sysPrimaryDefault,
  paddingRight: 24,
}));
